import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router'
import useUserDetail from 'hooks/use-user-detail'
import {
  Breadcrumb,
  Container,
  Button,
  Card,
  Row,
  Col,
  Spinner,
  InputGroup,
  FormControl,
  Alert,
  Collapse,
  ButtonGroup,
  Dropdown,
} from 'react-bootstrap'
import { useQueryClient } from 'react-query'
import useMutationAdminDeleteUser from 'hooks/use-mutation-admin-delete-user'
import useMutationAdminGenerateLogInUrl from 'hooks/use-mutation-admin-generate-log-in-url'
import useMutationAdminGenerateZeroLogInUrl from 'hooks/use-mutation-admin-generate-zero-log-in-url'
import useMutationAdminInvalidateUserAccess from 'hooks/use-mutation-admin-invalidate-user-access'
import useCardsForUser from 'hooks/use-cards-for-user'
import useMutationDeleteCard from 'hooks/use-mutation-delete-card'
import UserCardCard from 'components/user-card-card'
import useUserLinqProducts from 'hooks/use-get-user-linq-products'
import { useGetAllLinqProducts } from 'hooks/use-get-linq-products'
import useGetUserAuthConfirmation from 'hooks/use_get_user_auth_confirmation'
import UserOrgTeamTable from 'components/user-details-page/user-orgs-teams-table'
import UserActivatedProductsTable from 'components/user-details-page-old/user-activated-products-table'
import UserProfile from 'components/user-details-page/user-profile-card'
import UserAuthenticationTable from 'components/user-details-page-old/user-authentication-table'
import UserContactsTable from 'components/user-details-page/user-contacts-table'
import useGetUserContacts from 'hooks/use_get_user_contacts'
import useDebounce from 'hooks/use-debounce'
import useUserUserTeams from 'hooks/use-user-user-teams'
import useUserDeviceInfos from 'hooks/use_get_user_device_info'
import UserDeviceInfos from 'components/user-details-page/user-device-infos-table'
import toast from 'react-hot-toast'
import useUpdateUser from 'hooks/use-update-user-new'
import { PushTokensModal } from 'components/push-tokens-modal'
import useUserPushTokens from 'hooks/use-user-push-tokens'
import { PushToken } from 'interfaces/push-token'
import { PushTokenResultsModal } from 'components/push-token-results-modal'
import useSendPushTokenTest, {
  PushTokenTestData,
} from 'hooks/use-send-push-token-test'
import { EditPushTokenModal } from 'components/edit-push-token-modal'
import UserAppClipExperiencesTable from 'components/user-details-page/user-app-clip-experiences-table'
import useDeletePhoneNumber from 'hooks/use-delete-worknumber'
import useGetAppleIdByUserId from 'hooks/use-get-apple-id-by-user-id'
import useRecentUsers from 'hooks/use-recent-users'
import UserOrganizationCard from 'components/user-details-page/user-organization-card'
import { useGetInternalAdmins } from 'hooks/use-get_internal-admins'
import UserPhoneDetailsCard from 'components/user-details-page/user-phone-details-card'
import OrganizationIntegrationsCard from 'components/user-details-page/organization-integrations-card'
import hubspotLogo from 'images/hubspot-logo.svg'
import stripeLogo from 'images/stripe.svg'
import AdminToolsCard from 'components/user-details-page/admin-tool-card'
import UserSubscriptionCard from 'components/user-details-page/user-subscription-card'
import useGetPartnerToken from 'hooks/use-get-partner-token'

const UserDetailPage = () => {
  const queryClient = useQueryClient()
  const [signInLink, setSignInLink] = useState<any>()
  const [zeroSignInLink, setZeroSignInLink] = useState<any>()
  const history = useHistory()
  const { uuid } = useParams<any>()
  const [user, setUser] = useState<any>()
  const [userOrganizations, setUserOrganizations] = useState<any[]>([])
  const [userOrganization, setUserOrganization] = useState<any>()
  const [showPushTokensModal, setShowPushTokensModal] = useState(false)
  const [showPushTokenResultsModal, setShowPushTokenResultsModal] =
    useState(false)
  const [showEditPushTokenModal, setShowEditPushTokenModal] = useState(false)
  const [pushToken, setPushToken] = useState<PushToken>()
  const [pushTokenTestData, setPushTokenTestData] = useState<PushTokenTestData>(
    {
      title: '',
      body: '',
      image_url:
        'https://www.freepik.com/free-vector/cookies-logo-design-template_36295240.htm#page=2&query=funny%20logo&position=12&from_view=keyword&track=robertav1_2_sidr',
      data_link_route: 'https://linqapp.com/profile',
    }
  )

  // Add state for each card's collapse state
  const [showUserPages, setShowUserPages] = useState(true)
  const [showUserOrganizations, setShowUserOrganizations] = useState(true)
  const [showUserTeams, setShowUserTeams] = useState(true)
  const [showActivatedProducts, setShowActivatedProducts] = useState(true)
  const [showUserContacts, setShowUserContacts] = useState(true)
  const [showOrgIntegrations, setShowOrgIntegrations] = useState(true)
  const [showDeviceInfos, setShowDeviceInfos] = useState(true)
  const [showAppClipExperiences, setShowAppClipExperiences] = useState(true)

  const userDetailQuery = useUserDetail(uuid)
  const { data, isFetching } = userDetailQuery

  const { data: userActivatedLinqProducts } = useUserLinqProducts(uuid)
  const userLinqProducts = userActivatedLinqProducts?.data?.user_linq_products

  const { data: linqProducts } = useGetAllLinqProducts()
  const allLinqProducts = linqProducts?.linq_products

  const cardsForUserQuery = useCardsForUser(uuid)
  const cards = cardsForUserQuery?.data?.cards

  const { data: userTeams } = useUserUserTeams(uuid)

  const { data: authConfirmations, isLoading } =
    useGetUserAuthConfirmation(uuid)
  const userAuthConfirmations = authConfirmations?.data.auth_confirmations

  const { data: mobileDeviceInfos, isLoading: isMobileDeviceInfoLoading } =
    useUserDeviceInfos(uuid)

  const { data: pushTokens } = useUserPushTokens(uuid)

  const updateUser = useUpdateUser(user?.uuid)
  const { mutate: sendPushTokenTest } = useSendPushTokenTest(uuid)
  const { addRecentUser } = useRecentUsers()

  const { data: appleId } = useGetAppleIdByUserId(user?.id)

  const primaryOrg = userOrganization
  const hubspotUrl = primaryOrg?.internal_crm_url
  const orgId = primaryOrg?.id
  const { data: internalAdmins = [] } = useGetInternalAdmins()

  // Resolve account manager and sales rep names
  const accountManagerName = internalAdmins.find(
    (admin) => admin.id === primaryOrg?.account_manager_user_id
  )?.name

  const salesRepName = internalAdmins.find(
    (admin) => admin.id === primaryOrg?.organization_subscription?.sale_rep_id
  )?.name

  const { data: tokenData } = useGetPartnerToken(orgId?.toString() || '')
  const isSandbox = (tokenData?.data || []).some((i) => i.tier === 'sandbox')
  const isCli = (tokenData?.data || []).some((i) => i.tier === 'cli')

  useEffect(() => {
    if (data?.user) {
      setUser(data.user)
      setUserOrganization(data.user.organization)
      setUserOrganizations(data.user.user_organizations)
    }
  }, [
    data?.user?.id,
    data?.user?.name,
    data?.user?.email,
    data?.user?.phone_number,
  ])

  // Track user visit in a separate effect
  useEffect(() => {
    if (data?.user && data.user.id && data.user.name && data.user.email) {
      addRecentUser({
        id: data.user.id,
        name: data.user.name,
        email: data.user.email,
      })
    }
  }, [data?.user?.id]) // Only re-run when user ID changes

  // Generate Log in code (Bloop)
  const generateLogInCodeMutation = useMutationAdminGenerateLogInUrl({
    onSuccess: (data) => {
      setSignInLink(data.url)
      navigator.clipboard.writeText(data.url)
      toast.success('📋 Bloop login link copied to clipboard!')
    },
  })

  // Generate Log in code (Zero Web)
  const generateZeroLogInCodeMutation = useMutationAdminGenerateZeroLogInUrl({
    onSuccess: (data) => {
      setZeroSignInLink(data.url)
      navigator.clipboard.writeText(data.url)
      toast.success('📋 Zero Web login link copied to clipboard!')
    },
  })

  // Delete User
  const deleteUserMutation = useMutationAdminDeleteUser({
    onSuccess: (data, variables, context) => {
      toast(`User successfully deleted!`, { icon: `🗑` })
      history.replace('/users')
      queryClient.invalidateQueries(`admin/api/v1/users/search`)
    },
  })

  const invalidateUserAccessMutation = useMutationAdminInvalidateUserAccess({
    onSuccess: () => {
      toast(`User logged out of all devices successfully!`, { icon: `🔒` })
    },
  })

  const isInternalAdmin = user?.organization?.id === 11
  const deletePhoneNumber = useDeletePhoneNumber()

  const handleGetLoginLinkClicked = () => {
    if (signInLink) {
      // If link exists, copy it to clipboard with toast
      navigator.clipboard.writeText(signInLink)
      toast.success('📋 Bloop login link copied to clipboard!')
    } else {
      // Generate new link
      generateLogInCodeMutation.mutate(user?.uuid)
    }
  }

  const handleGetZeroLoginLinkClicked = () => {
    if (zeroSignInLink) {
      // If link exists, copy it to clipboard with toast
      navigator.clipboard.writeText(zeroSignInLink)
      toast.success('📋 Zero Web login link copied to clipboard!')
    } else {
      // Generate new link
      generateZeroLogInCodeMutation.mutate(user?.uuid)
    }
  }
  const deleteCardForUserMutation = useMutationDeleteCard({
    cardsForUserQueryKey: cardsForUserQuery.queryKey,
  })

  const handleClickCopy = async (code: string = '') => {
    try {
      await navigator.clipboard.writeText(code)
      toast(`${code} is copied`, { icon: `👍` })
    } catch (e) {
      toast(`Failed to copy`)
    }
  }

  const [contactQuery, setContactQuery] = useState('')
  const debouncedQuery = useDebounce(contactQuery, 500)
  const { data: userContacts = [], isFetching: contactsFetching } =
    useGetUserContacts({ uuid, query: debouncedQuery })

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setContactQuery(event.target.value)
  }

  const handleDisconnectUserStripeAccount = () => {
    updateUser.mutate({ stripe_user_id: null })
  }

  const handleInvalidateAllSessions = () => {
    if (user?.id) {
      const confirmed = window.confirm(
        `Are you sure you want to invalidate all sessions for ${
          user.name || user.email || 'this user'
        }?\n\nThis will:\n• Log the user out of all devices immediately\n• Force them to sign in again on all their devices\n• Cannot be undone\n\nClick OK to proceed or Cancel to abort.`
      )

      if (confirmed) {
        invalidateUserAccessMutation.mutate(user.id)
      }
    }
  }
  const handleSeePushTokenResultsClicked = (pushToken: PushToken) => {
    setPushToken(pushToken)
    setShowPushTokensModal(false)
    setShowPushTokenResultsModal(true)
  }
  const handleSendPushNotificationTest = (pushToken?: PushToken) => {
    sendPushTokenTest(
      {
        ...pushTokenTestData,
        title: pushTokenTestData.title || `This is a test from ${user.name} 😄`,
        body: pushTokenTestData.body || `The body is here!`,
        ...(pushToken && { push_token_id: pushToken.id }),
      },
      {
        onSuccess: () => {
          toast(`Push notification successfully sent`)
        },
      }
    )
  }

  const handleDeletePhoneNumber = (phoneNumberId: string) => {
    if (
      window.confirm(
        'Deleting this phone number will make it unusable by the user. They will then be able to claim a new number in the app.'
      )
    ) {
      deletePhoneNumber.mutate(phoneNumberId)
    }
  }

  return (
    <Container className="mb-4">
      <Breadcrumb>
        <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
        <Breadcrumb.Item href="/users">Users</Breadcrumb.Item>
        <Breadcrumb.Item active>{user?.name || `User Detail`}</Breadcrumb.Item>
      </Breadcrumb>
      <div className="d-flex flex-column flex-lg-row justify-content-lg-between align-items-lg-center gap-2 mb-1">
        <h1 className="mb-0">{user?.name}</h1>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <Dropdown as={ButtonGroup} size="sm">
            <Button variant="success" onClick={handleGetLoginLinkClicked}>
              {signInLink ? (
                <>
                  <ion-icon name="copy-outline" /> Copy Link
                </>
              ) : (
                'Ghost in as User'
              )}
            </Button>
            <Dropdown.Toggle split variant="success" id="ghost-in-dropdown" />
            <Dropdown.Menu>
              <Dropdown.Item onClick={handleGetZeroLoginLinkClicked}>
                {zeroSignInLink ? (
                  <>
                    <ion-icon name="copy-outline" /> Copy Zero Link
                  </>
                ) : (
                  'Ghost in as User (Zero)'
                )}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <Button
            variant="secondary"
            size="sm"
            href={`/user/${uuid}/blocked_numbers`}
          >
            Manage Blocked Numbers
          </Button>
        </div>
      </div>
      <div className="mb-1">
        <div className="d-flex gap-4 align-items-center user-actions-dropdown">
          {/* Stripe - only show if subscription exists */}
          {primaryOrg?.organization_subscription?.subscription_id && (
            <a
              href={`https://dashboard.stripe.com/subscriptions/${primaryOrg.organization_subscription.subscription_id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <div className="d-flex align-items-center gap-2">
                <img
                  src={stripeLogo}
                  alt="Stripe"
                  style={{ width: '20px', height: '20px' }}
                />
                <span
                  style={{
                    color: 'white',
                    fontSize: '0.875rem',
                    textDecoration: 'underline',
                    fontWeight: '500',
                  }}
                >
                  Stripe
                </span>
              </div>
            </a>
          )}

          {/* HubSpot */}
          {hubspotUrl ? (
            <a
              href={hubspotUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <div className="d-flex align-items-center gap-2">
                <img
                  src={hubspotLogo}
                  alt="HubSpot"
                  style={{ width: '20px', height: '20px' }}
                />
                <span
                  style={{
                    color: 'white',
                    fontSize: '0.875rem',
                    textDecoration: 'underline',
                    fontWeight: '500',
                  }}
                >
                  Hubspot
                </span>
              </div>
            </a>
          ) : null}
        </div>
      </div>
      {isSandbox && (
        <div
          className="text-center fw-bold mt-3"
          style={{
            backgroundColor: '#8B3A3A',
            color: '#fff',
            padding: '10px',
            borderRadius: '4px',
            fontSize: '0.85rem',
            letterSpacing: '1px',
            textTransform: 'uppercase',
          }}
        >
          Sandbox User — Limits Applied
        </div>
      )}
      {isCli && (
        <div
          className="text-center fw-bold mt-3"
          style={{
            backgroundColor: '#2E5984',
            color: '#fff',
            padding: '10px',
            borderRadius: '4px',
            fontSize: '0.85rem',
            letterSpacing: '1px',
            textTransform: 'uppercase',
          }}
        >
          CLI User — Shared Line
        </div>
      )}
      {isFetching ? (
        <p>Loading...</p>
      ) : (
        <>
          <Row>
            <Col lg="3">
              <UserProfile
                user={user}
                setShowPushTokensModal={setShowPushTokensModal}
                primaryOrg={primaryOrg}
              />
              <UserSubscriptionCard user={user} />
              <AdminToolsCard
                user={user}
                primaryOrg={primaryOrg}
                setShowPushTokensModal={setShowPushTokensModal}
              />
            </Col>
            <Col lg="6">
              <UserPhoneDetailsCard
                user={user}
                isInternalAdmin={isInternalAdmin}
                appleId={appleId}
                service={appleId ? 'Linq Blue' : 'Linq One'}
                worknumber={user?.worknumber}
                handleDeletePhoneNumber={handleDeletePhoneNumber}
                deletePhoneNumber={deletePhoneNumber}
                userName={user?.name}
                callRecordingEnabled={user?.recording_enabled}
                redactMessageHistory={primaryOrg?.redact_message_history}
                customerProductType={
                  primaryOrg?.organization_subscription?.customer_product_type
                }
              />
            </Col>
            <Col lg="3">
              <UserOrganizationCard
                org={primaryOrg}
                accountManagerName={accountManagerName}
                salesRepName={salesRepName}
                userName={user?.name}
              />
              <UserAuthenticationTable
                userAuthConfirmations={userAuthConfirmations}
                isLoading={isLoading}
                handleClickCopy={handleClickCopy}
                handleInvalidateAllSessions={handleInvalidateAllSessions}
              />
            </Col>
          </Row>
          {/* Organization Integrations Card */}
          <Card className="mb-2 mt-4">
            <Card.Header
              className="d-flex justify-content-between align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowOrgIntegrations(!showOrgIntegrations)}
            >
              <h4 className="mb-0">Organization Integrations</h4>
              <ion-icon
                name="chevron-down-outline"
                style={{
                  fontSize: '20px',
                  color: '#4da6ff',
                  transform: showOrgIntegrations
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)',
                  transition: 'transform 0.3s ease-in-out',
                }}
              />
            </Card.Header>
            <Collapse in={showOrgIntegrations} timeout={300}>
              <div>
                <Card.Body>
                  <OrganizationIntegrationsCard
                    orgId={primaryOrg?.id}
                    organizationName={primaryOrg?.name}
                    userName={user?.name}
                  />
                </Card.Body>
              </div>
            </Collapse>
          </Card>
          {/* User Device Infos Card */}
          <Card className="mb-2">
            <Card.Header
              className="d-flex justify-content-between align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowDeviceInfos(!showDeviceInfos)}
            >
              <h4 className="mb-0">User Device Infos</h4>
              <ion-icon
                name="chevron-down-outline"
                style={{
                  fontSize: '20px',
                  color: '#4da6ff',
                  transform: showDeviceInfos
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)',
                  transition: 'transform 0.3s ease-in-out',
                }}
              />
            </Card.Header>
            <Collapse in={showDeviceInfos} timeout={300}>
              <div>
                <Card.Body>
                  <UserDeviceInfos
                    mobileDeviceInfos={mobileDeviceInfos}
                    isLoading={isMobileDeviceInfoLoading}
                  />
                </Card.Body>
              </div>
            </Collapse>
          </Card>
          {/* User Pages Card */}
          <Card className="mb-2">
            <Card.Header
              className="d-flex justify-content-between align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowUserPages(!showUserPages)}
            >
              <h4 className="mb-0">User Pages</h4>
              <ion-icon
                name="chevron-down-outline"
                style={{
                  fontSize: '20px',
                  color: '#4da6ff',
                  transform: showUserPages ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease-in-out',
                }}
              />
            </Card.Header>
            <Collapse in={showUserPages} timeout={300}>
              <div>
                <Card.Body>
                  <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    {cardsForUserQuery.isFetching && (
                      <Spinner animation="border" role="status" />
                    )}
                    {cards &&
                      cards.map((card) => (
                        <UserCardCard
                          key={card.id}
                          card={card}
                          deleteCardForUserMutation={deleteCardForUserMutation}
                          user={user}
                          cardsForUserQueryKey={cardsForUserQuery.queryKey}
                        />
                      ))}
                  </div>
                </Card.Body>
              </div>
            </Collapse>
          </Card>

          {/* User's Organizations Card */}
          <Card className="mb-2">
            <Card.Header
              className="d-flex justify-content-between align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowUserOrganizations(!showUserOrganizations)}
            >
              <h4 className="mb-0">User's Organizations</h4>
              <ion-icon
                name="chevron-down-outline"
                style={{
                  fontSize: '20px',
                  color: '#4da6ff',
                  transform: showUserOrganizations
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)',
                  transition: 'transform 0.3s ease-in-out',
                }}
              />
            </Card.Header>
            <Collapse in={showUserOrganizations} timeout={300}>
              <div>
                <Card.Body>
                  {userOrganizations && (
                    <UserOrgTeamTable
                      tableName="Organization"
                      tableBodyData={userOrganizations}
                      tableBodyDataKey="organization"
                      link="organization"
                    />
                  )}
                </Card.Body>
              </div>
            </Collapse>
          </Card>

          {/* User Teams Card */}
          <Card className="mb-2">
            <Card.Header
              className="d-flex justify-content-between align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowUserTeams(!showUserTeams)}
            >
              <h4 className="mb-0">User Teams</h4>
              <ion-icon
                name="chevron-down-outline"
                style={{
                  fontSize: '20px',
                  color: '#4da6ff',
                  transform: showUserTeams ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease-in-out',
                }}
              />
            </Card.Header>
            <Collapse in={showUserTeams} timeout={300}>
              <div>
                <Card.Body>
                  {userTeams && (
                    <UserOrgTeamTable
                      tableName="Team"
                      tableBodyData={userTeams}
                      tableBodyDataKey="team"
                      link="team"
                    />
                  )}
                </Card.Body>
              </div>
            </Collapse>
          </Card>

          {/* Activated Products Card */}
          <Card className="mb-2">
            <Card.Header
              className="d-flex justify-content-between align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowActivatedProducts(!showActivatedProducts)}
            >
              <h4 className="mb-0">Activated Products</h4>
              <ion-icon
                name="chevron-down-outline"
                style={{
                  fontSize: '20px',
                  color: '#4da6ff',
                  transform: showActivatedProducts
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)',
                  transition: 'transform 0.3s ease-in-out',
                }}
              />
            </Card.Header>
            <Collapse in={showActivatedProducts} timeout={300}>
              <div>
                <Card.Body>
                  {userLinqProducts?.length > 0 ? (
                    <UserActivatedProductsTable
                      userLinqProducts={userLinqProducts}
                      allLinqProducts={allLinqProducts}
                      cards={cards}
                      userUUID={uuid}
                      user={user}
                    />
                  ) : (
                    <Card.Text>No Product Associates With This User</Card.Text>
                  )}
                </Card.Body>
              </div>
            </Collapse>
          </Card>

          <UserAppClipExperiencesTable
            userUuid={uuid}
            isExpanded={showAppClipExperiences}
            setIsExpanded={setShowAppClipExperiences}
          />

          {/* User's Contacts Card */}
          <Card className="mb-2">
            <Card.Header
              className="d-flex justify-content-between align-items-center"
              style={{ cursor: 'pointer', flexWrap: 'wrap', gap: '8px' }}
              onClick={() => setShowUserContacts(!showUserContacts)}
            >
              <h4 className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                User's Contacts
              </h4>
              <div className="d-flex align-items-center gap-3">
                {showUserContacts && (
                  <div
                    style={{ width: '200px', minWidth: '150px' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <InputGroup size="sm">
                      <FormControl
                        autoComplete="off"
                        onChange={handleSearchInputChange}
                        value={contactQuery}
                        type="text"
                        placeholder="Search Contacts"
                        aria-label="Search User Contact"
                      />
                    </InputGroup>
                  </div>
                )}
                <ion-icon
                  name="chevron-down-outline"
                  style={{
                    fontSize: '20px',
                    color: '#4da6ff',
                    transform: showUserContacts
                      ? 'rotate(180deg)'
                      : 'rotate(0deg)',
                    transition: 'transform 0.3s ease-in-out',
                  }}
                />
              </div>
            </Card.Header>
            <Collapse in={showUserContacts} timeout={300}>
              <div>
                <Card.Body
                  style={{ paddingBottom: contactsFetching ? '50%' : '10px' }}
                >
                  {contactsFetching && <Card.Text>Searching</Card.Text>}
                  {userContacts?.length > 0 && (
                    <UserContactsTable userContacts={userContacts} />
                  )}
                  {!contactsFetching && userContacts?.length === 0 && (
                    <Card.Text>No Contacts Associate With This User</Card.Text>
                  )}
                </Card.Body>
              </div>
            </Collapse>
          </Card>
        </>
      )}
      {user && pushTokens && (
        <>
          <PushTokensModal
            show={showPushTokensModal}
            onClose={() => setShowPushTokensModal(false)}
            user={user}
            pushTokens={pushTokens}
            handleSeePushTokenClicked={handleSeePushTokenResultsClicked}
            handleTestPushTokenClicked={handleSendPushNotificationTest}
            setShowEditPushTokenModal={setShowEditPushTokenModal}
          />
          <EditPushTokenModal
            user={user}
            show={showEditPushTokenModal}
            onClose={() => setShowEditPushTokenModal(false)}
            setPushTokenTestData={setPushTokenTestData}
          />
        </>
      )}
      {pushToken && (
        <PushTokenResultsModal
          show={showPushTokenResultsModal}
          onClose={() => setShowPushTokenResultsModal(false)}
          pushToken={pushToken}
        />
      )}
    </Container>
  )
}

export default UserDetailPage
